import React, { useEffect, useRef, useState } from 'react';
import { Fish, Beef, Flame, MapPin } from 'lucide-react';
import { images, translations, restaurantInfo } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';

const AboutSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  const { language } = useLanguage();
  const t = translations[language];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.15 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const stats = [
    { icon: Fish, value: '🐟', label: t.about.stats.fish },
    { icon: Beef, value: '🥩', label: t.about.stats.meat },
    { icon: Flame, value: '🔥', label: t.about.stats.pizza },
    { icon: MapPin, value: '📍', label: t.about.stats.location }
  ];

  return (
    <section
      id="chi-siamo"
      ref={sectionRef}
      className="py-28 bg-[#3d2617] relative overflow-hidden"
    >
      {/* Background decoration */}
      <div className="absolute inset-0">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#c9a227]/5 to-transparent"></div>
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-[#c9a227]/5 rounded-full blur-[80px]"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Section header with glow effect */}
        <div className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <span className={`inline-block text-[#c9a227] font-medium tracking-[0.2em] uppercase text-sm mb-4 transition-all duration-700 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
            {t.about.subtitle}
          </span>
          <h2 className={`font-serif text-4xl md:text-5xl font-bold text-white mb-6 transition-all duration-700 delay-200 ${isVisible ? 'section-title-glow visible' : ''}`}>
            {t.about.title}
          </h2>
          <div className={`w-24 h-0.5 bg-[#c9a227] mx-auto transition-all duration-700 delay-300 ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-0'}`}></div>
        </div>

        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Images grid */}
          <div className={`relative transition-all duration-1000 delay-200 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
            <div className="grid grid-cols-2 gap-4">
              <div className={`col-span-2 overflow-hidden transition-all duration-700 delay-300 ${isVisible ? 'card-illuminate visible' : 'card-illuminate'}`}>
                <img
                  src={images.polpo}
                  alt="Insalata di polpo"
                  className="w-full h-64 object-cover hover:scale-105 transition-transform duration-700"
                />
              </div>
              <div className={`overflow-hidden transition-all duration-700 delay-400 ${isVisible ? 'card-illuminate visible' : 'card-illuminate'}`}>
                <img
                  src={images.pasta}
                  alt="Pasta fresca"
                  className="w-full h-48 object-cover hover:scale-105 transition-transform duration-700"
                />
              </div>
              <div className={`overflow-hidden transition-all duration-700 delay-500 ${isVisible ? 'card-illuminate visible' : 'card-illuminate'}`}>
                <img
                  src={images.tagliata}
                  alt="Tagliata di manzo"
                  className="w-full h-48 object-cover hover:scale-105 transition-transform duration-700"
                />
              </div>
            </div>

            {/* Badge with glow */}
            <div className={`absolute -top-4 -right-4 bg-[#c9a227] text-white px-6 py-4 shadow-2xl transition-all duration-700 delay-600 ${isVisible ? 'opacity-100 scale-100 animate-pulse-glow' : 'opacity-0 scale-0'}`}>
              <p className="font-serif text-xl font-bold">{language === 'it' ? 'Famiglia' : 'Familie'}</p>
              <p className="text-sm uppercase tracking-wider">Rosi & Andrea</p>
            </div>
          </div>

          {/* Content section */}
          <div className={`transition-all duration-1000 delay-400 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
            <h3 className={`font-serif text-3xl font-bold text-white mb-6 transition-all duration-700 ${isVisible ? 'section-title-glow visible' : ''}`}>
              {t.about.welcome}
            </h3>
            
            <div className="space-y-5 text-white/80 text-lg leading-relaxed mb-8">
              <p className={`transition-all duration-700 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`}>{t.about.p1}</p>
              <p className={`transition-all duration-700 delay-600 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`}>{t.about.p2}</p>
              <p className={`transition-all duration-700 delay-650 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`}>{t.about.p3}</p>
            </div>

            {/* Quote with glow */}
            <blockquote className={`border-l-2 border-[#c9a227] pl-6 py-4 mb-10 transition-all duration-700 delay-700 ${isVisible ? 'opacity-100 translate-x-0 bg-[#c9a227]/10' : 'opacity-0 -translate-x-5'}`}>
              <p className="font-elegant text-2xl italic text-[#e8d48b]">
                "{t.about.quote}"
              </p>
            </blockquote>

            {/* Stats grid with illuminate effect */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {stats.map((stat, index) => (
                <div
                  key={stat.label}
                  className={`text-center p-5 bg-white/5 border border-white/10 transition-all duration-500 hover:border-[#c9a227] hover:bg-[#c9a227]/10 hover:shadow-[0_0_20px_rgba(201,162,39,0.2)] ${isVisible ? 'card-illuminate visible' : 'card-illuminate'}`}
                  style={{ transitionDelay: `${800 + index * 100}ms` }}
                >
                  <p className="text-3xl mb-2">{stat.value}</p>
                  <p className="text-xs text-white/60 uppercase tracking-wider">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
